/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Checkbox,
  FormControl,
  Hidden,
  Radio,
  Select,
  Text,
  TextArea,
  TextField,
  View,
} from '@az/starc-ui'
import { SelectOption } from '@az/starc-ui/dist/components/select/Select.types'
import { Direction } from '@az/starc-ui/dist/components/view/View.types'
import { Responsive } from '@az/starc-ui/dist/types/global'
import { ReactElement } from 'react'
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormStateReturn,
} from 'react-hook-form'
import { InputForm } from './InputForm'
import { InputSubmit } from './InputSubmit'

type Columns = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 'auto'
interface Props {
  name?: string
  label?: string
  formId?: string
  disabled?: boolean
  hideLabel?: boolean
  columns?: Responsive<Columns>
  width?: Responsive<string>
  height?: Responsive<string>
  readonly?: boolean
  helperText?: string
}
interface ControllerProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  onChangeConfirm?: () => boolean
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >
  hideErrors?: boolean
  errorMessage?: string
  hasError?: boolean
}
interface ControllerCustomFieldProps<T extends FieldValues> {
  type: 'custom'
  render: ({
    field,
    fieldState,
    formState,
  }: {
    field: ControllerRenderProps<T, (string | undefined) & Path<T>>
    fieldState: ControllerFieldState
    formState: UseFormStateReturn<T>
  }) => React.ReactElement
  options?: never
  direction?: never
  gap?: never
  startElement?: never
  endElement?: never
  inputType?: never
  inputAttributes?: never
}
interface UncontrolledCustomFieldProps {
  type: 'custom'
  value?: never
  onChange?: never
  endElement?: never
  inputType?: never
  startElement?: never
  options?: never
  direction?: never
  gap?: never
  inputAttributes?: never
}

interface UncontrolledTextFieldProps {
  type: 'text' | 'text-area'
  value: string
  startElement?: ReactElement
  endElement?: ReactElement
  inputType?: React.HTMLInputTypeAttribute
  onChange: (value: string) => void
  options?: never
  direction?: never
  gap?: never
  inputAttributes?: React.InputHTMLAttributes<HTMLInputElement> & {
    ref?: React.Ref<HTMLInputElement>
  }
}
interface ControlledTextFieldProps {
  type: 'text' | 'text-area'
  startElement?: ReactElement
  endElement?: ReactElement
  inputType?: React.HTMLInputTypeAttribute
  options?: never
  direction?: never
  gap?: never
  render?: never
  inputAttributes?: React.InputHTMLAttributes<HTMLInputElement> & {
    ref?: React.Ref<HTMLInputElement>
  }
}
interface UncontrolledSingleOptionProps {
  value: string | null
  onChange: (value: string | undefined) => void
  options: SelectOption[]
  gap?: number
  endElement?: never
  inputType?: never
  inputAttributes?: never
}
interface ControlledSingleOptionProps {
  options: SelectOption[]
  gap?: number
  startElement?: never
  endElement?: never
  inputType?: never
  render?: never
  inputAttributes?: never
}

interface UncontrolledMultipleOptionProps {
  value: string[] | null
  onChange: (value: string[]) => void
  options: SelectOption[]
  gap?: number
  startElement?: never
  endElement?: never
  inputType?: never
  inputAttributes?: never
}
interface ControlledMultipleOptionProps {
  options: SelectOption[]
  gap?: number
  startElement?: never
  endElement?: never
  inputType?: never
  render?: never
  inputAttributes?: never
}
interface ControlledSelectProps extends ControlledSingleOptionProps {
  type: 'select'
  direction?: never
  gap?: never
  startElement?: never
  endElement?: never
  inputType?: never
  inputAttributes?: never
}
interface UncontrolledSelectProps extends UncontrolledSingleOptionProps {
  type: 'select'
  direction?: never
  gap?: never
  startElement?: ReactElement
  endElement?: never
  inputType?: never
  inputAttributes?: never
}

interface ControlledRadioProps extends ControlledSingleOptionProps {
  type: 'radio'
  direction?: Direction
  gap?: number
  startElement?: never
  endElement?: never
  inputType?: never
  inputAttributes?: never
}

interface UncontrolledRadioProps extends UncontrolledSingleOptionProps {
  type: 'radio'
  name: string
  direction?: Direction
  gap?: number
  endElement?: never
  inputType?: never
  startElement?: never
  inputAttributes?: never
}

interface UncontrolledCheckboxProps extends UncontrolledMultipleOptionProps {
  type: 'checkbox'
  name: string
  direction?: Direction
  gap?: number
  endElement?: never
  inputType?: never
  startElement?: never
  inputAttributes?: never
}
interface ControlledCheckboxProps extends ControlledMultipleOptionProps {
  type: 'checkbox'
  direction?: Direction
  gap?: number
  endElement?: never
  inputType?: never
  startElement?: never
  inputAttributes?: never
}

type FieldProps = Props &
  (
    | UncontrolledTextFieldProps
    | UncontrolledSelectProps
    | UncontrolledRadioProps
    | UncontrolledCheckboxProps
    | UncontrolledCustomFieldProps
  )

type ControlledFieldProps<T extends FieldValues> = Props &
  ControllerProps<T> &
  (
    | ControlledTextFieldProps
    | ControlledSelectProps
    | ControlledRadioProps
    | ControlledCheckboxProps
    | ControllerCustomFieldProps<T>
  )

export const Input = (props: FieldProps) => {
  const {
    name,
    readonly,
    formId,
    label,
    type,
    value,
    inputAttributes,
    onChange,
    endElement,
    inputType,
    startElement,
    options,
    direction,
    disabled,
    gap,
  } = props
  if (type === 'text') {
    return (
      <TextField
        name={name}
        label={label}
        value={value}
        onValueChange={onChange}
        disabled={disabled}
        startElement={startElement}
        endElement={endElement}
        type={inputType}
        inputAttributes={{
          form: formId,
          readOnly: readonly,
          ...inputAttributes,
        }}
      />
    )
  }
  if (type === 'text-area') {
    return (
      <TextArea
        id={name || ''}
        name={name}
        label={label || ''}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        inputAttributes={{
          readOnly: readonly,
        }}
      />
    )
  }
  if (type === 'select') {
    return (
      <Select
        name={name}
        label={label || ''}
        startElement={startElement}
        value={options?.find(option => option.value === value) || null}
        onValueChange={value => onChange(value?.value)}
        options={options || []}
        disabled={disabled}
        inputAttributes={{
          readOnly: readonly,
        }}
      />
    )
  }
  if (type === 'radio') {
    return (
      <Radio.Group
        name={name}
        value={value ?? undefined}
        onValueChange={onChange}
        disabled={disabled}
      >
        <View direction={direction} gap={gap}>
          {options?.map(option => (
            <View key={option.value}>
              <Radio
                value={option.value}
                label={option.label}
                disabled={option.disabled}
                inputAttributes={{
                  readOnly: readonly,
                }}
              />
            </View>
          ))}
        </View>
      </Radio.Group>
    )
  }
  if (type === 'checkbox') {
    return (
      <Checkbox.Group name={name} value={value ?? []} onValueChange={onChange} disabled={disabled}>
        <View direction={direction} gap={gap}>
          {options?.map(option => (
            <View key={option.value}>
              <Checkbox
                value={option.value}
                label={option.label}
                disabled={option.disabled}
                inputAttributes={{
                  readOnly: readonly,
                }}
              />
            </View>
          ))}
        </View>
      </Checkbox.Group>
    )
  }

  return null
}

interface WrapperProps {
  fieldProps: FieldProps
  settings: WrapperSettings
}
interface WrapperSettings {
  isRequired?: boolean
  render?: () => ReactElement
}

const InputWrapper = ({ fieldProps, settings }: WrapperProps) => {
  return (
    <View.Item columns={fieldProps.columns || 12}>
      <View width={fieldProps.width} height={fieldProps.height} justify="center">
        <Hidden hide={fieldProps.hideLabel || !fieldProps.label}>
          <View direction="row" gap={1}>
            <Text variant="display-5">{fieldProps.label}</Text>
            <Text variant="display-5" color="error">
              {settings.isRequired ? '*' : null}
            </Text>
          </View>
          <View padding={[3, 0]} />
        </Hidden>
        {settings.render ? settings.render() : <Input {...fieldProps} />}
      </View>
    </View.Item>
  )
}

const InputController = <T extends FieldValues>({
  control,
  errorMessage,
  hasError,
  name,
  formId,
  type,
  readonly,
  disabled,
  label,
  hideLabel,
  options,
  rules,
  direction,
  gap,
  hideErrors,
  width,
  height,
  columns,
  startElement,
  helperText,
  endElement,
  inputType,
  render,
  inputAttributes,
  onChangeConfirm,
}: ControlledFieldProps<T>) => {
  const commonProps = {
    label,
    hideLabel,
    formId,
    readonly,
    width,
    height,
    columns,
    helperText,
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState, formState }) => {
        const onChange = (value: any) => {
          if (onChangeConfirm !== undefined && onChangeConfirm() === false) {
            return
          }
          field.onChange(value)
        }

        const isRequired = !!rules?.required

        const controllerProps = {
          disabled: formState.disabled || disabled,
        }
        const settings: WrapperSettings = {
          isRequired,
        }

        return (
          <FormControl
            hasError={hasError != undefined ? hasError : Boolean(fieldState.error)}
            required={isRequired}
          >
            {type === 'custom' ? (
              <InputWrapper
                fieldProps={{ ...commonProps, type: 'custom' }}
                settings={{
                  ...settings,
                  render: () => render({ field, fieldState, formState }),
                }}
              />
            ) : null}
            {type === 'text' || type === 'text-area' ? (
              <>
                <InputWrapper
                  settings={settings}
                  fieldProps={{
                    ...field,
                    ...commonProps,
                    ...controllerProps,
                    inputAttributes,
                    type,
                    startElement,
                    endElement,
                    inputType,
                    onChange,
                  }}
                />
              </>
            ) : null}
            {type === 'select' ? (
              <>
                <InputWrapper
                  settings={settings}
                  fieldProps={{
                    ...field,
                    ...commonProps,
                    ...controllerProps,
                    type,
                    options,
                    onChange,
                  }}
                />
              </>
            ) : null}
            {type === 'radio' || type === 'checkbox' ? (
              <>
                <InputWrapper
                  settings={settings}
                  fieldProps={{
                    ...field,
                    ...commonProps,
                    ...controllerProps,
                    type,
                    options,
                    direction,
                    gap,
                    onChange,
                  }}
                />
              </>
            ) : null}
            <Hidden hide={hideErrors}>
              <FormControl.Error>{errorMessage || fieldState.error?.message}</FormControl.Error>
              <FormControl.Helper>{helperText}</FormControl.Helper>
            </Hidden>
          </FormControl>
        )
      }}
    ></Controller>
  )
}

Input.Controller = InputController
Input.Submit = InputSubmit
Input.Form = InputForm
